import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { environment } from '@env/environment';
import { GoogleAnalyticalService } from 'app/dp-world/services/google-analytics.service';
import { LoadExternalScriptService } from 'app/dp-world/services/load-external-script.service';
import { MilestoneService } from 'app/dp-world/services/milestone.service';
import { UiService } from 'app/dp-world/services/ui.service';
import { AuthService } from 'app/routes/sessions/login/auth.service';
import { User } from 'app/routes/sessions/login/user.model';
import { Subject, takeUntil } from 'rxjs';
import { PreloaderService } from './preloader.service';

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  user: User;
  currentTenantCode: string;
  showReadyStatusUI: boolean = Boolean(false);
  serviceProviderDTF = environment.serviceProviderCode;
  serviceProviderDTFCM = environment.serviceProviderCodeCayman;
  isUserRegistrationEnabled = false;
  private unSubscribeService: Subject<void> = new Subject();

  constructor(private authService: AuthService,
    private loadExternalScriptService: LoadExternalScriptService,
    private ui: UiService,
    private googleAnalytics: GoogleAnalyticalService,
    private milestoneService: MilestoneService,
    private route: ActivatedRoute,
    private preloader: PreloaderService,
    private router: Router) { }

  async resolveRootRoute(route: ActivatedRouteSnapshot) {
    if (window.location.host === 'tradefinance.dpworld.com' || window.location.host === 'qa-tradefinance.dpworld.com') {
      this.router.navigate(['']);
      return false;
    }
    try {
      this.user = this.authService.currentUserValue;
      if (!this.user) {
        if (this.isPublicRoute()) {
          this.preloader.hide();
          return true;
        } else {
          const queryParams = route.queryParamMap['params'];
          this.authService.setRouteParams(queryParams);
          if (queryParams['state'] && queryParams['session_state'] && queryParams['code']) {
            this.loginToCargoesFinance(queryParams);
            return true;
          } else {
            if (window.location.host === 'tradefinance.dpworld.com' || window.location.host === 'qa-tradefinance.dpworld.com') {
              this.router.navigate(['']);
              return false;
            }
            this.authService.navigateToClientRegistration();
            return false;
          }
        }
      } else {
        return { user: this.user };
      }
    } catch (error) {
      return false;
    }
  }

  loginToCargoesFinance(loginParams: { state: any; sessionState: any; code: any }): void {
    const params = {
      code: loginParams.code,
      redirect_uri: environment.clientRegistrationRedirectionURL + 'home/def',
    };
    const decodedUrl = atob(loginParams.state);
    // Remove demo once backend changes are done
    if (decodedUrl?.includes('/lending')) {
      params['is_lending_url'] = true;
    }
    if (decodedUrl?.includes('companyReference')) {
      let parsedDecodedUrl = JSON.parse(decodeURIComponent(decodedUrl));
      const url = parsedDecodedUrl.returnUrl;
      if (url) {
        const index = url.indexOf('companyReference');
        const compRefStrLength = 17; // length of companyReference=
        const companyReferenceId = url.substring((index + compRefStrLength), url.length)
        if (companyReferenceId.length > 0) {
          params['company_reference'] = companyReferenceId;
        }
      }
    }
    this.authService
      .loginToCargoesFinance(params)
      .pipe(takeUntil(this.unSubscribeService))
      .subscribe(
        (newUser: User) => {
          if (!newUser) {
            this.ui.snackbar("The backend isn't allowing logins right now.");
            return;
          }
          this.user = newUser;
          const gtmTag = {
            event: 'dl_login',
            click_id: 'login',
            click_text: 'User Login',
            userId: this.user.user_id,
            eventdetail: {
              'eventCategory': 'Login',
              'eventAction': 'Success'
            }
          };
          this.currentTenantCode = this.authService.getTenantCode();
          this.googleAnalytics.pushTag(gtmTag);
          this.authService.idleCount = 0;
          this.authService.refreshDataCount = 0;
          this.authService.startIdleTimer();

          this.milestoneService.checkForEvent(environment.milestones.login);
          // if org and user account are setup, let them in
          if (!this.showReadyStatusUI) {
            if (!this.authService.isLoggedInToAccountingModule() && !this.authService.isLoggedInToBHSModule() &&
              (this.user.organisation_type === 'BANK' &&
                this.user.accepted_terms_version[this.currentTenantCode].FIStandardTerms ===
                undefined) ||
              (this.user.organisation_type === 'ORG' && this.authService.isClientAgreementPending())
            ) {
              if (
                this.currentTenantCode === this.serviceProviderDTFCM &&
                this.user.companyStatus &&
                this.authService.pendingStatuses.includes(this.user.companyStatus.name)
              ) {
                const navToURL =
                  this.user.organisation_type === 'ORG' ? '/apply/def' : '/dashboard';
                location.href = navToURL;
              } else {
                if (this.router.url?.includes('home/def')) {
                  location.href = 'home/def';
                } else {
                  this.clearCurrentRoute();
                }
              }
              return;
            } else {
              let returnUrl = '';
              if (this.authService.isLoggedInToAccountingModule()) {
                if (decodedUrl.includes('{"')) {
                  const queryParams = JSON.parse(decodedUrl);
                  returnUrl = queryParams.returnUrl ? queryParams.returnUrl : decodedUrl;
                } else {
                  returnUrl = '/acc-rec';
                }
              } else if (this.authService.isLoggedInToBHSModule()) {
                if (decodedUrl.includes('{"')) {
                  const queryParams = JSON.parse(decodedUrl);
                  returnUrl = queryParams.returnUrl ? queryParams.returnUrl : decodedUrl;
                } else {
                  returnUrl = '/acc-rec';
                }
              } else {
                if (decodedUrl.includes('{"')) {
                  const queryParams = JSON.parse(decodedUrl);
                  returnUrl = queryParams.returnUrl ? queryParams.returnUrl : decodedUrl;
                  if (returnUrl.includes('/register-company/view?companyReference=')) {
                    const switchingTenant = this.processCompRegistrationReq(returnUrl);
                    if (switchingTenant) {
                      return;
                    }
                    returnUrl = '/register-company/view';
                  } else if (returnUrl.includes('/credit-limit/module-arranging') || returnUrl.includes('/credit-limit/module-lending')) {
                    localStorage.setItem(environment.storage.creditLimitNotification, 'true');
                  }
                } else if (decodedUrl.includes('/home/def')) {
                  returnUrl = '/dashboard';
                } else if (
                  this.currentTenantCode === this.serviceProviderDTFCM &&
                  this.user.organisation_type === 'ORG' &&
                  this.user.companyStatus &&
                  this.authService.pendingStatuses.includes(this.user.companyStatus.name) &&
                  this.user.accepted_terms_version &&
                  this.user.accepted_terms_version[this.currentTenantCode] &&
                  this.user.accepted_terms_version[this.currentTenantCode].TermsAndCondition &&
                  this.user.accepted_terms_version[this.currentTenantCode].TermsAndCondition.length >
                  0
                ) {
                  returnUrl = '/apply/def';
                } else {
                  returnUrl = decodedUrl;
                }
              }
              location.href = returnUrl;
            }
          } else {
            if (
              this.currentTenantCode === this.serviceProviderDTFCM &&
              this.user.companyStatus &&
              this.authService.pendingStatuses.includes(this.user.companyStatus.name)
            ) {
              if (this.authService.isLoggedInToAccountingModule()) {
                this.router.navigate(['/acc-rec']);
              } else if (this.authService.isLoggedInToBHSModule()) {
                this.router.navigate(['bhs']);
              } else {
                const navToURL =
                  this.user.organisation_type === 'ORG' ? '/apply/def' : '/dashboard';
                this.router.navigate([navToURL]);
              }
            } else {
              if (this.router.url?.includes('home/def')) {
                location.href = 'home/def';
              } else {
                this.clearCurrentRoute();
              }
            }
          }
        },
        error => {
          if (error && error.error && error.error.errorMessage) {
            if (
              error.error.errorCode === '50090' &&
              error.error.errorMessage.includes('Please try again')
            ) {
              this.ui.snackbar(
                'An error occurred while processing your request. Please logout and try again.'
              );
            } else if (isNaN(error.error.errorMessage)) {
              this.ui.snackbar(error.error.errorMessage);
            } else {
              let userFailedLoginAttempts = parseInt(error.error.errorMessage, 10);
              console.log('userFailedLoginAttempts: ' + userFailedLoginAttempts);
              this.ui.snackbar('Invalid username or password');
            }
          } else {
            this.ui.snackbar('Invalid username or password');
            setTimeout(() => {
              this.authService.logout();
            }, 2000);
          }
          this.authService.logout();
        }
      );
  }

  processCompRegistrationReq(redirectUrl: string): boolean {
    const encodedRefId = redirectUrl.replace('/register-company/view?companyReference=', '');
    const mawaniReferenceId = atob(decodeURIComponent(encodedRefId));
    if (this.user.mawani_org_reference_id !== mawaniReferenceId) {
      const findOrgInstance = this.user.multipleOrgDetails.findIndex((instance) => instance.companyReference === mawaniReferenceId);
      if (findOrgInstance !== -1) {
        this.authService.triggerOrgSwitch(mawaniReferenceId, 'notificationUrlSwitch');
        return true;
      }
    }
    return false;
  }

  clearCurrentRoute(): void {
    this.router.navigate(['.'], { relativeTo: this.route, queryParams: null }).then(() => {
      this.preloader.hide();
    });
  }

  ngOnDestroy() {
    this.ui.closeCurrentSnackbar();
    this.unSubscribeService.next();
    this.unSubscribeService.unsubscribe();
  }

  isPublicRoute(): boolean {
    if (!location?.href) {
      return false;
    }
    return location.href.includes('/create-account')
      || location.href.includes('/policies')
      || location.href.includes('terms-and-conditions')
      || location.href.includes('privacy-and-policy')
      || location.href.includes('customer-disclaimer')
      || location.href.includes('cookies-policy')
      || location.href.includes('terms-agreement')
      || location.href.includes('client-agreement')
      || location.href.includes('fi-agreement')
      || location.href.includes('counter-party-acceptance')
      || location.href.includes('finance-accept-terms');
  }
}
